import { defineStore } from "pinia"
import { inject, ref } from "vue"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import type { Services } from "@services"
import type {
    AlarmConfig,
    AlarmEntityProfil,
    ThresholdPoint,
} from "@modules/energy-manager/types/alarme"

type AlarmTarget =
    | { scope: "building"; id: string }
    | { scope: "source"; id: string }

/** Snapshot pour restaurer `profil` / `alarme` si le DELETE échoue après suppression optimiste. */
type ThresholdRemovalSnapshot = {
    profilPoints?: ThresholdPoint[]
    alarm?: AlarmConfig
}

/** Payload renvoyé par POST/PUT profil (document building ou source avec `profil`). */
type ProfilMutationApiEntity = {
    profil?: AlarmEntityProfil
    _id?: string
    _id_building?: string
    _id_source?: string
} | null

export const useAlarmeStore = defineStore("alarme", () => {
    const services = inject<Services>("services")!
    const buildingStore = useBuildingStore()
    const sourceStore = useSourceStore()
    const pendingVariable = ref<string | null>(null)
    const lastError = ref<string | null>(null)

    async function refreshTarget(target: AlarmTarget): Promise<void> {
        if (target.scope === "building") {
            await buildingStore.fetchBuilding(target.id, { silent: true })
        } else {
            await sourceStore.fetchSourceById({
                _id_source: target.id,
                silent: true,
            })
        }
    }

    /**
     * Applique `profil` renvoyé par POST/PUT profil sur le bâtiment ou la source en mémoire.
     * @returns true si le store a été mis à jour
     */
    function applyProfilFromApiResponse(
        target: AlarmTarget,
        entity: ProfilMutationApiEntity | undefined
    ): boolean {
        if (!entity?.profil) return false
        if (target.scope === "building") {
            const b = buildingStore.building
            if (!b) return false
            const sameBuilding =
                String(b._id_building) === String(target.id) ||
                String(b._id) === String(target.id) ||
                (entity._id_building != null &&
                    String(b._id_building) === String(entity._id_building)) ||
                (entity._id != null && String(b._id) === String(entity._id))
            if (!sameBuilding) return false
            b.profil = entity.profil
            return true
        }
        const src = sourceStore.sources.find(
            (s) => String(s._id_source) === String(target.id)
        )
        if (!src) return false
        if (
            entity._id_source != null &&
            String(src._id_source) !== String(entity._id_source)
        ) {
            return false
        }
        src.profil = entity.profil
        return true
    }

    /**
     * Lit la config d’alarme courante pour une variable (bâtiment ou source en mémoire).
     */
    function getAlarmConfigFromClient(
        target: AlarmTarget,
        variable: string
    ): AlarmConfig {
        if (target.scope === "building") {
            return {
                ...(buildingStore.building?.alarme?.[variable] ?? {}),
            }
        }
        const src = sourceStore.sources.find(
            (s) => String(s._id_source) === String(target.id)
        )
        return { ...(src?.alarme?.[variable] ?? {}) }
    }

    /**
     * Fusionne une mise à jour partielle dans `alarme[variable]` (Pinia / objet API) sans requête réseau.
     * Utilisé pour les mises à jour optimistes (ex. options seuils).
     */
    function patchAlarmConfigLocal(
        target: AlarmTarget,
        variable: string,
        partial: Partial<AlarmConfig>
    ): void {
        const merged: AlarmConfig = {
            ...getAlarmConfigFromClient(target, variable),
            ...partial,
        }
        if (target.scope === "building") {
            const b = buildingStore.building
            if (!b) return
            if (!b.alarme) b.alarme = {}
            b.alarme[variable] = merged
        } else {
            const src = sourceStore.sources.find(
                (s) => String(s._id_source) === String(target.id)
            )
            if (!src) return
            if (!src.alarme) src.alarme = {}
            src.alarme[variable] = merged
        }
    }

    /**
     * Retire la variable des seuils (`profil`) et de la config d’alarme en mémoire, sans requête réseau.
     * Aligné sur le comportement de `updateAlarm` : éviter un `fetchBuilding` / `fetchSource` après suppression API.
     */
    function removeThresholdLocal(target: AlarmTarget, variable: string): void {
        if (target.scope === "building") {
            const b = buildingStore.building
            if (!b) return
            if (b.profil && variable in b.profil) {
                delete b.profil[variable]
            }
            if (b.alarme && variable in b.alarme) {
                delete b.alarme[variable]
            }
        } else {
            const src = sourceStore.sources.find(
                (s) => String(s._id_source) === String(target.id)
            )
            if (!src) return
            if (src.profil && variable in src.profil) {
                delete src.profil[variable]
            }
            if (src.alarme && variable in src.alarme) {
                delete src.alarme[variable]
            }
        }
    }

    /**
     * Copie l’état local de la variable avant suppression optimiste (pour rollback).
     */
    function captureThresholdRemovalSnapshot(
        target: AlarmTarget,
        variable: string
    ): ThresholdRemovalSnapshot | null {
        if (target.scope === "building") {
            const b = buildingStore.building
            if (!b) return null
            const snap: ThresholdRemovalSnapshot = {}
            if (b.profil && variable in b.profil) {
                snap.profilPoints = b.profil[variable]!.map((p) => ({ ...p }))
            }
            if (b.alarme && variable in b.alarme) {
                snap.alarm = { ...b.alarme[variable] }
            }
            if (snap.profilPoints === undefined && snap.alarm === undefined) {
                return null
            }
            return snap
        }
        const src = sourceStore.sources.find(
            (s) => String(s._id_source) === String(target.id)
        )
        if (!src) return null
        const snap: ThresholdRemovalSnapshot = {}
        if (src.profil && variable in src.profil) {
            snap.profilPoints = src.profil[variable]!.map((p) => ({ ...p }))
        }
        if (src.alarme && variable in src.alarme) {
            snap.alarm = { ...src.alarme[variable] }
        }
        if (snap.profilPoints === undefined && snap.alarm === undefined) {
            return null
        }
        return snap
    }

    /**
     * Réinjecte profil / alarme après échec API (liste réaffiche la ligne).
     */
    function restoreThresholdRemovalSnapshot(
        target: AlarmTarget,
        variable: string,
        snapshot: ThresholdRemovalSnapshot
    ): void {
        if (target.scope === "building") {
            const b = buildingStore.building
            if (!b) return
            if (snapshot.profilPoints !== undefined) {
                if (!b.profil) b.profil = {}
                b.profil[variable] = snapshot.profilPoints.map((p) => ({
                    ...p,
                }))
            }
            if (snapshot.alarm !== undefined) {
                if (!b.alarme) b.alarme = {}
                b.alarme[variable] = { ...snapshot.alarm }
            }
            return
        }
        const src = sourceStore.sources.find(
            (s) => String(s._id_source) === String(target.id)
        )
        if (!src) return
        if (snapshot.profilPoints !== undefined) {
            if (!src.profil) src.profil = {}
            src.profil[variable] = snapshot.profilPoints.map((p) => ({ ...p }))
        }
        if (snapshot.alarm !== undefined) {
            if (!src.alarme) src.alarme = {}
            src.alarme[variable] = { ...snapshot.alarm }
        }
    }

    async function addThreshold(
        target: AlarmTarget,
        variable: string,
        points: ThresholdPoint[],
        options?: { signal?: AbortSignal }
    ): Promise<void> {
        pendingVariable.value = variable
        lastError.value = null
        try {
            const updated =
                target.scope === "building"
                    ? await services.$addBuildingThreshold(
                          target.id,
                          variable,
                          points,
                          options
                      )
                    : await services.$addSourceThreshold(
                          target.id,
                          variable,
                          points
                      )
            if (!applyProfilFromApiResponse(target, updated)) {
                await refreshTarget(target)
            }
        } catch (error) {
            lastError.value = (error as Error).message
            throw error
        } finally {
            pendingVariable.value = null
        }
    }

    async function updateThreshold(
        target: AlarmTarget,
        variable: string,
        points: ThresholdPoint[]
    ): Promise<void> {
        pendingVariable.value = variable
        lastError.value = null
        try {
            const updated =
                target.scope === "building"
                    ? await services.$updateBuildingThreshold(
                          target.id,
                          variable,
                          points
                      )
                    : await services.$updateSourceThreshold(
                          target.id,
                          variable,
                          points
                      )
            if (!applyProfilFromApiResponse(target, updated)) {
                await refreshTarget(target)
            }
        } catch (error) {
            lastError.value = (error as Error).message
            throw error
        } finally {
            pendingVariable.value = null
        }
    }

    async function removeThreshold(
        target: AlarmTarget,
        variable: string
    ): Promise<void> {
        pendingVariable.value = variable
        lastError.value = null
        const snapshot = captureThresholdRemovalSnapshot(target, variable)
        const useOptimistic = snapshot != null
        if (useOptimistic) {
            removeThresholdLocal(target, variable)
        }
        try {
            if (target.scope === "building") {
                await services.$removeBuildingThreshold(target.id, variable)
            } else {
                await services.$removeSourceThreshold(target.id, variable)
            }
            if (!useOptimistic) {
                removeThresholdLocal(target, variable)
            }
        } catch (error) {
            lastError.value = (error as Error).message
            if (useOptimistic && snapshot) {
                restoreThresholdRemovalSnapshot(target, variable, snapshot)
            }
            throw error
        } finally {
            pendingVariable.value = null
        }
    }

    /**
     * Met à jour l’alarme côté API puis fusionne dans le store (pas de re-fetch par défaut).
     * Le PUT alarme ne renvoie pas l’entité : on applique le payload localement pour éviter
     * un `fetchBuilding` / `fetchSource` qui remonte toute la page et réinitialise l’UI.
     */
    async function updateAlarm(
        target: AlarmTarget,
        variable: string,
        alarm: AlarmConfig,
        options?: { refreshAfter?: boolean; signal?: AbortSignal }
    ): Promise<void> {
        pendingVariable.value = variable
        lastError.value = null
        try {
            if (target.scope === "building") {
                await services.$updateBuildingAlarm(
                    target.id,
                    variable,
                    alarm,
                    { signal: options?.signal }
                )
            } else {
                await services.$updateSourceAlarm(target.id, variable, alarm)
            }
            patchAlarmConfigLocal(target, variable, alarm)
            if (options?.refreshAfter) {
                await refreshTarget(target)
            }
        } catch (error) {
            lastError.value = (error as Error).message
            throw error
        } finally {
            pendingVariable.value = null
        }
    }

    async function subscribe(
        target: AlarmTarget,
        variable: string,
        key: string
    ) {
        if (target.scope === "building") {
            await services.$subscribeBuildingAlarm(target.id, variable, key)
        } else {
            await services.$subscribeSourceAlarm(target.id, variable, key)
        }
        await refreshTarget(target)
    }

    async function unsubscribe(
        target: AlarmTarget,
        variable: string,
        key: string
    ) {
        if (target.scope === "building") {
            await services.$unsubscribeBuildingAlarm(target.id, variable, key)
        } else {
            await services.$unsubscribeSourceAlarm(target.id, variable, key)
        }
        await refreshTarget(target)
    }

    /**
     * Réinitialise le store à son état initial (ex. déconnexion).
     */
    function $reset(): void {
        pendingVariable.value = null
        lastError.value = null
    }

    return {
        pendingVariable,
        lastError,
        addThreshold,
        updateThreshold,
        removeThreshold,
        updateAlarm,
        getAlarmConfigFromClient,
        patchAlarmConfigLocal,
        subscribe,
        unsubscribe,
        $reset,
    }
})
